import {
  PublicKey,
  TransactionInstruction,
  SystemProgram,
} from '@solana/web3.js';
import { Buffer } from 'buffer';

// ============================================================================
// Program IDs and constants — mirrored from evore-sdk so this app is self-contained
// ============================================================================

export const EVORE_PROGRAM_ID = new PublicKey(
  '8jaLKWLJAj5jVCZbxpe3zRUvLB3LD48MRtaQ2AjfCfxa'
);
export const ORE_PROGRAM_ID = new PublicKey(
  'oreV3EG1i9BEgiAJ8b177Z2S2rMarzak4NMv1kULvWv'
);
export const ORE_MINT_ADDRESS = new PublicKey(
  'oreoU2P8bN6jkk3jbaiVxYnG1dCXcYxwhwyK9jSybcp'
);
export const ORE_TREASURY_ADDRESS = new PublicKey(
  '45db2FSR4mcXdSVVZbKbwojU6uYDpMyhpEi7cC8nHaWG'
);
export const TOKEN_PROGRAM_ID = new PublicKey(
  'TokenkegQfeZyiNwAJbNbGKPFXCWuBvf9Ss623VQ5DA'
);
export const ASSOCIATED_TOKEN_PROGRAM_ID = new PublicKey(
  'ATokenGPvbdGVxr1b2hvZbsiqW5xWH25efTNsLJA8knL'
);

const MANAGED_MINER_AUTH_SEED = 'managed-miner-auth';
const DEPLOYER_SEED = 'deployer';
const ORE_MINER_SEED = 'miner';
const ORE_BOARD_SEED = 'board';
const ORE_ROUND_SEED = 'round';
const ORE_TREASURY_SEED = 'treasury';
const ORE_AUTOMATION_SEED = 'automation';

const MANAGER_DATA_SIZE = 40;
const MANAGER_AUTHORITY_OFFSET = 8;

export const MMA_RENT_LAMPORTS = 890_880n;
export const LAMPORTS_PER_SOL = 1_000_000_000n;
export const ORE_DECIMALS = 11;

/** I80F48 fixed-point: 80-bit integer + 48-bit fraction. Used for the
 *  rewards-factor fields that drive live-refined inference. */
export const I80F48_PRECISION = 1n << 48n;

/** Claim-time refine fee on `rewardsOre` — users receive 90% of unrefined. */
export const REFINE_FEE_NUMERATOR = 9n;
export const REFINE_FEE_DENOMINATOR = 10n;

const EvoreInstruction = {
  MMCheckpoint: 2,
  MMClaimSOL: 3,
  MMClaimORE: 4,
  UpdateDeployer: 6,
  WithdrawAutodeployBalance: 10,
} as const;

// ============================================================================
// PDA helpers
// ============================================================================

function bigintToLeBytes(value: bigint): Buffer {
  const buf = Buffer.alloc(8);
  buf.writeBigUInt64LE(value);
  return buf;
}

export function getManagedMinerAuthPda(
  manager: PublicKey,
  authId: bigint
): [PublicKey, number] {
  return PublicKey.findProgramAddressSync(
    [
      Buffer.from(MANAGED_MINER_AUTH_SEED),
      manager.toBuffer(),
      bigintToLeBytes(authId),
    ],
    EVORE_PROGRAM_ID
  );
}

export function getDeployerPda(manager: PublicKey): [PublicKey, number] {
  return PublicKey.findProgramAddressSync(
    [Buffer.from(DEPLOYER_SEED), manager.toBuffer()],
    EVORE_PROGRAM_ID
  );
}

export function getOreMinerPda(authority: PublicKey): [PublicKey, number] {
  return PublicKey.findProgramAddressSync(
    [Buffer.from(ORE_MINER_SEED), authority.toBuffer()],
    ORE_PROGRAM_ID
  );
}

export function getOreBoardPda(): [PublicKey, number] {
  return PublicKey.findProgramAddressSync(
    [Buffer.from(ORE_BOARD_SEED)],
    ORE_PROGRAM_ID
  );
}

export function getOreRoundPda(roundId: bigint): [PublicKey, number] {
  return PublicKey.findProgramAddressSync(
    [Buffer.from(ORE_ROUND_SEED), bigintToLeBytes(roundId)],
    ORE_PROGRAM_ID
  );
}

/**
 * ORE automation PDA for a given authority (here: the managed-miner-auth).
 *
 * Required by ORE's `process_checkpoint` since the "checkpoint v2" account
 * layout. EVORE's `MMCheckpoint` routes to `ore_api::checkpoint_v2()` only
 * when this account is supplied as a 10th key; with 9 keys it falls back to
 * the legacy `ore_api::checkpoint()`, whose 6-account CPI no longer matches
 * ORE's 8-account handler and fails with NotEnoughAccountKeys.
 */
export function getOreAutomationPda(authority: PublicKey): [PublicKey, number] {
  return PublicKey.findProgramAddressSync(
    [Buffer.from(ORE_AUTOMATION_SEED), authority.toBuffer()],
    ORE_PROGRAM_ID
  );
}

export function getOreTreasuryPda(): [PublicKey, number] {
  return PublicKey.findProgramAddressSync(
    [Buffer.from(ORE_TREASURY_SEED)],
    ORE_PROGRAM_ID
  );
}

export function getAssociatedTokenAddress(
  wallet: PublicKey,
  mint: PublicKey
): PublicKey {
  const [ata] = PublicKey.findProgramAddressSync(
    [wallet.toBuffer(), TOKEN_PROGRAM_ID.toBuffer(), mint.toBuffer()],
    ASSOCIATED_TOKEN_PROGRAM_ID
  );
  return ata;
}

export function getOreTokenAddress(wallet: PublicKey): PublicKey {
  return getAssociatedTokenAddress(wallet, ORE_MINT_ADDRESS);
}

// ============================================================================
// Account decoders
// ============================================================================

export interface ManagerAccount {
  authority: PublicKey;
}

export function decodeManager(data: Uint8Array): ManagerAccount {
  return { authority: new PublicKey(data.slice(8, 40)) };
}

export interface DeployerAccount {
  managerKey: PublicKey;
  deployAuthority: PublicKey;
  bpsFee: bigint;
  flatFee: bigint;
  expectedBpsFee: bigint;
  expectedFlatFee: bigint;
}

export function decodeDeployer(data: Uint8Array): DeployerAccount {
  const buf = Buffer.from(data);
  return {
    managerKey: new PublicKey(buf.slice(8, 40)),
    deployAuthority: new PublicKey(buf.slice(40, 72)),
    bpsFee: buf.readBigUInt64LE(72),
    flatFee: buf.readBigUInt64LE(80),
    expectedBpsFee: buf.readBigUInt64LE(88),
    expectedFlatFee: buf.readBigUInt64LE(96),
  };
}

export interface OreMinerSnapshot {
  roundId: bigint;
  checkpointId: bigint;
  /** Pending SOL rewards in the miner PDA (lamports). */
  rewardsSol: bigint;
  /** Pending ORE rewards not yet refined (smallest units, 11 decimals). */
  rewardsOre: bigint;
  /** On-chain refined ORE — STALE between checkpoints. Use `inferLiveRefinedOre()`
   *  with the treasury factor to get the live value. */
  refinedOre: bigint;
  /** Miner's snapshot of the global refining factor (raw I80F48 as 128-bit BigInt). */
  rewardsFactor: bigint;
}

/**
 * Decode the ORE Miner fields the sunset site needs: roundId/checkpointId for
 * deciding whether to checkpoint before claim, and refinedOre/rewardsOre for
 * displaying balances.
 *
 * Layout mirrors the deployed ORE program's `Miner` struct
 * (regolith-labs/ore api/src/state/miner.rs — Steel 8-byte discriminator +
 * #[repr(C)], `Numeric` = [u8; 16], total 752 bytes). The previous layout was
 * stale: it omitted the `auto_return` field and the `mass[25]` array, so every
 * reward offset was shifted and rewardsOre/refinedOre were read out of the
 * middle of `cumulative[25]` (lifetime running totals) — the cause of the
 * wildly inaccurate balances users reported.
 *   off  size  field
 *     0     8  discriminator
 *     8    32  authority
 *    40     8  autoReturn
 *    48     8  checkpointId
 *    56     8  checkpointFee
 *    64   200  deployed[25]   (25 × u64)
 *   264   200  mass[25]       (25 × u64)
 *   464   200  cumulative[25] (25 × u64)
 *   664     8  roundId
 *   672    16  rewardsFactor  (Numeric / I80F48)
 *   688     8  rewardsSol
 *   696     8  refinedOre
 *   704     8  rewardsOre
 *   712     8  lastClaimOreAt
 *   720     8  lastClaimSolAt
 *   728     8  lifetimeRewardsOre
 *   736     8  lifetimeDeployed
 *   744     8  lifetimeRewardsSol
 *   752  = total account size
 */
export function decodeOreMinerSnapshot(data: Uint8Array): OreMinerSnapshot {
  const buf = Buffer.from(data);
  return {
    checkpointId: buf.readBigUInt64LE(48),
    roundId: buf.readBigUInt64LE(664),
    rewardsFactor: readI80F48(buf, 672),
    rewardsSol: buf.readBigUInt64LE(688),
    refinedOre: buf.readBigUInt64LE(696),
    rewardsOre: buf.readBigUInt64LE(704),
  };
}

export interface OreTreasurySnapshot {
  /** Global refining factor that advances continuously as miners earn ORE.
   *  Combined with each miner's snapshot in `inferLiveRefinedOre()` to compute
   *  the true current refined balance. Raw I80F48 as 128-bit BigInt. */
  minerRewardsFactor: bigint;
}

/**
 * Decode the ORE Treasury fields needed for live-refined inference.
 *
 * Layout mirrors the deployed ORE program's `Treasury` struct
 * (regolith-labs/ore api/src/state/treasury.rs — Steel 8-byte discriminator +
 * #[repr(C)], `Numeric` = [u8; 16], total 48 bytes). The previous layout was
 * stale (extra balance/buffer/stake fields), which placed minerRewardsFactor
 * at offset 32 — where `total_refined` now sits — breaking the inference.
 *   off  size  field
 *     0     8  discriminator
 *     8     8  motherlode
 *    16    16  minerRewardsFactor (Numeric / I80F48)
 *    32     8  total_refined
 *    40     8  total_unclaimed
 *    48  = total account size
 */
export function decodeOreTreasurySnapshot(data: Uint8Array): OreTreasurySnapshot {
  const buf = Buffer.from(data);
  return {
    minerRewardsFactor: readI80F48(buf, 16),
  };
}

/** Read a 16-byte I80F48 (signed 128-bit fixed-point, 48 fractional bits) as
 *  an unsigned 128-bit BigInt. Per the ORE program math the factors are
 *  effectively unsigned; the `delta < 0` guard in `inferLiveRefinedOre()`
 *  handles any unexpected ordering. */
function readI80F48(buf: Buffer, offset: number): bigint {
  const low = buf.readBigUInt64LE(offset);
  const high = buf.readBigUInt64LE(offset + 8);
  return (high << 64n) | low;
}

/**
 * Compute the true live refined-ORE balance from a miner snapshot + the
 * current treasury factor. The on-chain `refinedOre` is only correct as of
 * the miner's last checkpoint; between checkpoints, refining accrues against
 * the global factor and we recover it with:
 *
 *   accrued = max(0, treasury.factor - miner.factor) * rewardsOre / 2^48
 *   live    = miner.refinedOre + accrued
 *
 * Ported verbatim from `server/src/services/mining/ore-api.ts:106-124`
 * (`inferRefinedOre`) so sunset matches the main-app number exactly.
 */
export function inferLiveRefinedOre(
  miner: OreMinerSnapshot,
  treasury: OreTreasurySnapshot
): bigint {
  const delta = treasury.minerRewardsFactor - miner.rewardsFactor;
  if (delta < 0n) return miner.refinedOre;
  return miner.refinedOre + (delta * miner.rewardsOre) / I80F48_PRECISION;
}

/** What the user actually receives if they claim everything right now:
 *  90% of unrefined `rewardsOre` plus the live refined balance. */
export function computeClaimableOre(
  miner: OreMinerSnapshot,
  treasury: OreTreasurySnapshot
): bigint {
  const liveRefined = inferLiveRefinedOre(miner, treasury);
  return (
    (miner.rewardsOre * REFINE_FEE_NUMERATOR) / REFINE_FEE_DENOMINATOR +
    liveRefined
  );
}

export interface SplTokenAccount {
  mint: PublicKey;
  owner: PublicKey;
  amount: bigint;
}

export function decodeTokenAccount(data: Uint8Array): SplTokenAccount {
  const buf = Buffer.from(data);
  return {
    mint: new PublicKey(buf.slice(0, 32)),
    owner: new PublicKey(buf.slice(32, 64)),
    amount: buf.readBigUInt64LE(64),
  };
}

// ============================================================================
// getProgramAccounts filter for finding managers owned by a user
// ============================================================================

export function managerAccountsFilter(authority: PublicKey) {
  return {
    filters: [
      { dataSize: MANAGER_DATA_SIZE },
      {
        memcmp: {
          offset: MANAGER_AUTHORITY_OFFSET,
          bytes: authority.toBase58(),
        },
      },
    ],
  };
}

// ============================================================================
// Instruction builders — only the four needed for sunset operations
// ============================================================================

/**
 * Checkpoint a finished round so its pending SOL rewards roll into the miner's
 * `rewardsSol` accumulator (which `claim_sol` then sweeps into the MMA). Must
 * be sent BEFORE `mmClaimSol` whenever the on-chain miner has `roundId > checkpointId`.
 */
export function mmCheckpointInstruction(
  signer: PublicKey,
  manager: PublicKey,
  roundId: bigint,
  authId: bigint = 0n
): TransactionInstruction {
  const [managedMinerAuth, bump] = getManagedMinerAuthPda(manager, authId);
  const [oreMiner] = getOreMinerPda(managedMinerAuth);
  const [oreBoard] = getOreBoardPda();
  const [oreRound] = getOreRoundPda(roundId);
  const [oreAutomation] = getOreAutomationPda(managedMinerAuth);

  const data = Buffer.alloc(10);
  data[0] = EvoreInstruction.MMCheckpoint;
  data.writeBigUInt64LE(authId, 1);
  data[9] = bump;

  return new TransactionInstruction({
    programId: EVORE_PROGRAM_ID,
    keys: [
      { pubkey: signer, isSigner: true, isWritable: true },
      { pubkey: manager, isSigner: false, isWritable: true },
      { pubkey: managedMinerAuth, isSigner: false, isWritable: true },
      { pubkey: oreMiner, isSigner: false, isWritable: true },
      { pubkey: ORE_TREASURY_ADDRESS, isSigner: false, isWritable: true },
      { pubkey: oreBoard, isSigner: false, isWritable: true },
      { pubkey: oreRound, isSigner: false, isWritable: true },
      { pubkey: SystemProgram.programId, isSigner: false, isWritable: false },
      { pubkey: ORE_PROGRAM_ID, isSigner: false, isWritable: false },
      // 10th key opts EVORE into ORE's checkpoint-v2 CPI. Omitting it selects
      // the legacy path, whose 6-account CPI fails with NotEnoughAccountKeys.
      { pubkey: oreAutomation, isSigner: false, isWritable: true },
    ],
    data,
  });
}

/**
 * NOTE: Account list follows the patched MineMore-server SDK
 * (server/src/lib/evore-sdk/instructions.ts), which is the version that
 * matches the *deployed* Evore program. The public `evore-sdk` npm package
 * is stale — it omits the oreBoard account at index 3 and the on-chain
 * program rejects with "insufficient account keys for instruction".
 */
export function mmClaimSolInstruction(
  signer: PublicKey,
  manager: PublicKey,
  authId: bigint = 0n
): TransactionInstruction {
  const [managedMinerAuth, bump] = getManagedMinerAuthPda(manager, authId);
  const [oreMiner] = getOreMinerPda(managedMinerAuth);
  const [oreBoard] = getOreBoardPda();

  const data = Buffer.alloc(10);
  data[0] = EvoreInstruction.MMClaimSOL;
  data.writeBigUInt64LE(authId, 1);
  data[9] = bump;

  return new TransactionInstruction({
    programId: EVORE_PROGRAM_ID,
    keys: [
      { pubkey: signer, isSigner: true, isWritable: true },
      { pubkey: manager, isSigner: false, isWritable: true },
      { pubkey: managedMinerAuth, isSigner: false, isWritable: true },
      { pubkey: oreBoard, isSigner: false, isWritable: true },
      { pubkey: oreMiner, isSigner: false, isWritable: true },
      { pubkey: SystemProgram.programId, isSigner: false, isWritable: false },
      { pubkey: ORE_PROGRAM_ID, isSigner: false, isWritable: false },
    ],
    data,
  });
}

/** Account list follows the patched MineMore-server SDK — see note on
 *  mmClaimSolInstruction above. The deployed program needs oreBoard at index 3. */
export function mmClaimOreInstruction(
  signer: PublicKey,
  manager: PublicKey,
  authId: bigint = 0n
): TransactionInstruction {
  const [managedMinerAuth, bump] = getManagedMinerAuthPda(manager, authId);
  const [oreMiner] = getOreMinerPda(managedMinerAuth);
  const [oreBoard] = getOreBoardPda();
  const [treasury] = getOreTreasuryPda();
  const treasuryTokens = getOreTokenAddress(treasury);
  const recipientTokens = getOreTokenAddress(managedMinerAuth);
  const signerTokens = getOreTokenAddress(signer);

  const data = Buffer.alloc(10);
  data[0] = EvoreInstruction.MMClaimORE;
  data.writeBigUInt64LE(authId, 1);
  data[9] = bump;

  return new TransactionInstruction({
    programId: EVORE_PROGRAM_ID,
    keys: [
      { pubkey: signer, isSigner: true, isWritable: true },
      { pubkey: manager, isSigner: false, isWritable: true },
      { pubkey: managedMinerAuth, isSigner: false, isWritable: true },
      { pubkey: oreBoard, isSigner: false, isWritable: true },
      { pubkey: oreMiner, isSigner: false, isWritable: true },
      { pubkey: ORE_MINT_ADDRESS, isSigner: false, isWritable: true },
      { pubkey: recipientTokens, isSigner: false, isWritable: true },
      { pubkey: signerTokens, isSigner: false, isWritable: true },
      { pubkey: treasury, isSigner: false, isWritable: true },
      { pubkey: treasuryTokens, isSigner: false, isWritable: true },
      { pubkey: SystemProgram.programId, isSigner: false, isWritable: false },
      { pubkey: TOKEN_PROGRAM_ID, isSigner: false, isWritable: false },
      { pubkey: ASSOCIATED_TOKEN_PROGRAM_ID, isSigner: false, isWritable: false },
      { pubkey: ORE_PROGRAM_ID, isSigner: false, isWritable: false },
    ],
    data,
  });
}

export function updateDeployerInstruction(
  signer: PublicKey,
  manager: PublicKey,
  newDeployAuthority: PublicKey,
  newBpsFee: bigint,
  newFlatFee: bigint = 0n,
  newExpectedBpsFee: bigint = 0n,
  newExpectedFlatFee: bigint = 0n
): TransactionInstruction {
  const [deployerPda] = getDeployerPda(manager);

  const data = Buffer.alloc(33);
  data[0] = EvoreInstruction.UpdateDeployer;
  data.writeBigUInt64LE(newBpsFee, 1);
  data.writeBigUInt64LE(newFlatFee, 9);
  data.writeBigUInt64LE(newExpectedBpsFee, 17);
  data.writeBigUInt64LE(newExpectedFlatFee, 25);

  return new TransactionInstruction({
    programId: EVORE_PROGRAM_ID,
    keys: [
      { pubkey: signer, isSigner: true, isWritable: true },
      { pubkey: manager, isSigner: false, isWritable: true },
      { pubkey: deployerPda, isSigner: false, isWritable: true },
      { pubkey: newDeployAuthority, isSigner: false, isWritable: false },
      { pubkey: SystemProgram.programId, isSigner: false, isWritable: false },
    ],
    data,
  });
}

export function withdrawAutodeployBalanceInstruction(
  signer: PublicKey,
  manager: PublicKey,
  authId: bigint,
  amount: bigint
): TransactionInstruction {
  const [managedMinerAuth] = getManagedMinerAuthPda(manager, authId);

  const data = Buffer.alloc(17);
  data[0] = EvoreInstruction.WithdrawAutodeployBalance;
  data.writeBigUInt64LE(authId, 1);
  data.writeBigUInt64LE(amount, 9);

  return new TransactionInstruction({
    programId: EVORE_PROGRAM_ID,
    keys: [
      { pubkey: signer, isSigner: true, isWritable: true },
      { pubkey: manager, isSigner: false, isWritable: true },
      { pubkey: managedMinerAuth, isSigner: false, isWritable: true },
      { pubkey: SystemProgram.programId, isSigner: false, isWritable: false },
    ],
    data,
  });
}

// ============================================================================
// ORE-program-direct claim instructions (legacy / non-Evore miners)
// ============================================================================
// These target the ORE program directly with the wallet as the OreMiner's
// authority. For Evore subaccounts the authority is a ManagedMinerAuth PDA
// and you must go through the `mmClaim*` wrappers above; for legacy Privy
// miners the wallet IS the authority and can sign for ORE-program calls
// directly. Discriminators + account layouts ported from
// server/src/services/mining/ore-api.ts (buildClaimSolInstruction,
// buildClaimOreInstruction).
//
// Per memory `legacy-ore-mining-mechanics`: NO checkpoint preamble needed
// for legacy claim_sol — that's an Evore-specific concern.

const OreInstruction = {
  Checkpoint: 2,
  ClaimSOL: 3,
  ClaimORE: 4,
} as const;

/** Build the ORE program's `claim_sol` instruction for a legacy miner.
 *  Sweeps `OreMiner.rewards_sol` from the PDA into the wallet's lamport
 *  balance. Signer is the wallet itself. */
export function claimSolInstruction(wallet: PublicKey): TransactionInstruction {
  const [oreMiner] = getOreMinerPda(wallet);
  const [oreBoard] = getOreBoardPda();

  return new TransactionInstruction({
    programId: ORE_PROGRAM_ID,
    keys: [
      { pubkey: wallet, isSigner: true, isWritable: true },
      { pubkey: oreBoard, isSigner: false, isWritable: true },
      { pubkey: oreMiner, isSigner: false, isWritable: true },
      { pubkey: SystemProgram.programId, isSigner: false, isWritable: false },
      { pubkey: ORE_PROGRAM_ID, isSigner: false, isWritable: false },
    ],
    data: Buffer.from([OreInstruction.ClaimSOL]),
  });
}

/** Build the ORE program's `claim_ore` instruction for a legacy miner.
 *  Moves the OreMiner PDA's refined ORE into the wallet's ORE ATA. The
 *  on-chain program creates the recipient ATA via CPI if it doesn't exist
 *  yet (Token + ATA programs are in the account list for this reason). */
export function claimOreInstruction(wallet: PublicKey): TransactionInstruction {
  const [oreMiner] = getOreMinerPda(wallet);
  const [oreBoard] = getOreBoardPda();
  const [treasury] = getOreTreasuryPda();
  const recipientTokens = getOreTokenAddress(wallet);
  const treasuryTokens = getOreTokenAddress(treasury);

  return new TransactionInstruction({
    programId: ORE_PROGRAM_ID,
    keys: [
      { pubkey: wallet, isSigner: true, isWritable: true },
      { pubkey: oreBoard, isSigner: false, isWritable: true },
      { pubkey: oreMiner, isSigner: false, isWritable: true },
      { pubkey: ORE_MINT_ADDRESS, isSigner: false, isWritable: false },
      { pubkey: recipientTokens, isSigner: false, isWritable: true },
      { pubkey: treasury, isSigner: false, isWritable: true },
      { pubkey: treasuryTokens, isSigner: false, isWritable: true },
      { pubkey: SystemProgram.programId, isSigner: false, isWritable: false },
      { pubkey: TOKEN_PROGRAM_ID, isSigner: false, isWritable: false },
      { pubkey: ASSOCIATED_TOKEN_PROGRAM_ID, isSigner: false, isWritable: false },
      { pubkey: ORE_PROGRAM_ID, isSigner: false, isWritable: false },
    ],
    data: Buffer.from([OreInstruction.ClaimORE]),
  });
}

// ============================================================================
// Formatting helpers
// ============================================================================

export function formatSol(lamports: bigint, decimals = 6): string {
  return (Number(lamports) / Number(LAMPORTS_PER_SOL)).toFixed(decimals);
}

export function formatOre(amount: bigint, decimals = 6): string {
  return (Number(amount) / 10 ** ORE_DECIMALS).toFixed(decimals);
}

export function formatUsd(value: number, decimals = 2): string {
  return `$${value.toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  })}`;
}

export function shortenPubkey(pubkey: PublicKey | string, chars = 4): string {
  const s = pubkey.toString();
  return `${s.slice(0, chars)}...${s.slice(-chars)}`;
}
